import React from "react";
import ReactDOM from "react-dom";
import { MDBRow, MDBCol } from "mdb-react-ui-kit";
import "./LightboxGallery.css";

/**
 * Componente reutilizable para visualizar una galería de imágenes con
 * lightbox.  Permite definir opciones de configuración como mostrar
 * miniaturas o indicadores, habilitar la descarga de imágenes y
 * establecer un tamaño base para las miniaturas.  El contenido de
 * la galería proviene de la prop `items`, que es un array de cadenas
 * base64 (con o sin prefijo data:).
 *
 * Props:
 * - items (array<string>): lista de imágenes en base64.
 * - showThumbnails (boolean): si se muestran miniaturas en el lightbox.
 * - showIndicators (boolean): si se muestran puntos indicadores.
 * - downloadable (boolean): si se muestra un botón para descargar la imagen.
 * - imageHeight (number|string): altura de cada miniatura en la cuadrícula.
 * - singleRow (boolean): si se muestra en una sola fila horizontal con scroll.
 * - visibleCount (number): nº de elementos visibles en modo singleRow (default 4).
 * - showViewAll (boolean): muestra un botón "Ver todo" que abre un popup con la galería completa.
 */
class LightboxGallery extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentIndex: null,
      viewAllOpen: false,
    };
    this.rowRef = React.createRef();
  }

  /** Desplaza la fila horizontal un "paso" (ancho visible) hacia la izquierda o derecha */
  scrollRow = (direction) => {
    const container = this.rowRef.current;
    if (!container) return;
    const step = container.clientWidth * 0.8;
    container.scrollBy({ left: direction === 'right' ? step : -step, behavior: 'smooth' });
  };

  // Normaliza base64 o URL a src válido para <img>
  getSrc = (b64) => {
    if (typeof b64 !== "string") return "";
    const s = b64.trim();
    if (!s) return "";
    if (s.startsWith("data:") || s.startsWith("http://") || s.startsWith("https://") || s.startsWith("/")) return s;
    return `data:image/jpeg;base64,${s}`; // por defecto JPEG
  };

  openImage = (index) => {
    this.setState({ currentIndex: index });
  };

  closeLightbox = () => {
    this.setState({ currentIndex: null });
  };

  prevImage = (e) => {
    e.stopPropagation();
    const { items } = this.props;
    this.setState((prevState) => {
      const prev = prevState.currentIndex;
      if (prev === 0) return { currentIndex: items.length - 1 };
      return { currentIndex: prev - 1 };
    });
  };

  nextImage = (e) => {
    e.stopPropagation();
    const { items } = this.props;
    this.setState((prevState) => {
      const prev = prevState.currentIndex;
      if (prev === items.length - 1) return { currentIndex: 0 };
      return { currentIndex: prev + 1 };
    });
  };

  handleKeyDown = (e) => {
    const { currentIndex, viewAllOpen } = this.state;
    if (e.key === "Escape") {
      if (currentIndex !== null) {
        this.closeLightbox();
      } else if (viewAllOpen) {
        this.setState({ viewAllOpen: false });
      }
      return;
    }
    if (currentIndex === null) return;
    if (e.key === "ArrowLeft") {
      this.prevImage(e);
    } else if (e.key === "ArrowRight") {
      this.nextImage(e);
    } else if (e.key === "Escape") {
      this.closeLightbox();
    }
  };

  componentDidMount() {
    window.addEventListener("keydown", this.handleKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.handleKeyDown);
  }

  render() {
    const {
      items = [],
      showThumbnails = true,
      showIndicators = false,
      downloadable = false,
      imageHeight,
      singleRow = false,
      visibleCount = 4,
      showViewAll = false,
    } = this.props;
    const { currentIndex, viewAllOpen } = this.state;

    // === NUEVO (mínimo): construye la lista de data URLs
    const sources = items.map(this.getSrc).filter(Boolean);

    // Tamaño de cada celda en modo singleRow: ocupa 1/visibleCount del ancho
    // con un pequeño gap entre ellas.
    const cellStyle = singleRow
      ? { flex: `0 0 calc(${100 / visibleCount}% - 8px)`, maxWidth: `calc(${100 / visibleCount}% - 8px)` }
      : undefined;

    return (
      <div className="lightbox-gallery">
        {/* Cuadrícula de miniaturas en la página principal */}
        {singleRow ? (
          <>
          <div className="lightbox-gallery-row-wrapper">
            {sources.length > visibleCount && (
              <button
                className="lightbox-row-nav left"
                onClick={() => this.scrollRow('left')}
                aria-label="Anterior"
              >
                <i className="fas fa-chevron-left" />
              </button>
            )}
            <div className="lightbox-gallery-row" ref={this.rowRef}>
              {sources.map((src, idx) => (
                <div key={idx} className="lightbox-gallery-row-item" style={cellStyle}>
                  <img
                    src={src}
                    alt={`galería ${idx + 1}`}
                    className="lightbox-gallery-image"
                    style={imageHeight != null ? { height: imageHeight } : undefined}
                    onClick={() => this.openImage(idx)}
                  />
                </div>
              ))}
            </div>
            {sources.length > visibleCount && (
              <button
                className="lightbox-row-nav right"
                onClick={() => this.scrollRow('right')}
                aria-label="Siguiente"
              >
                <i className="fas fa-chevron-right" />
              </button>
            )}
          </div>
          {showViewAll && sources.length > visibleCount && (
            <div className="lightbox-gallery-viewall-bar">
              <button
                className="lightbox-gallery-viewall-btn"
                onClick={() => this.setState({ viewAllOpen: true })}
              >
                <i className="fas fa-th me-1" />
                Ver todo ({sources.length})
              </button>
            </div>
          )}
          </>
        ) : (
          <MDBRow className="gy-3">
            {sources.map((src, idx) => (
              <MDBCol key={idx} sm="6" md="4" lg="3">
                <img
                  src={src}
                  alt={`galería ${idx + 1}`}
                  className="lightbox-gallery-image"
                  style={imageHeight != null ? { height: imageHeight } : undefined}
                  onClick={() => this.openImage(idx)}
                />
              </MDBCol>
            ))}
          </MDBRow>
        )}

        {/* Modal "Ver todo" — portal al body */}
        {viewAllOpen && ReactDOM.createPortal(
          <div
            className="lightbox-viewall-overlay"
            onClick={() => this.setState({ viewAllOpen: false })}
          >
            <div
              className="lightbox-viewall-panel"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="lightbox-viewall-header">
                <h3 className="lightbox-viewall-title">Galería de fotos</h3>
                <button
                  className="lightbox-viewall-close"
                  onClick={() => this.setState({ viewAllOpen: false })}
                  aria-label="Cerrar"
                >
                  <i className="fas fa-times" />
                </button>
              </div>
              <div className="lightbox-viewall-grid">
                {sources.map((src, idx) => (
                  <img
                    key={idx}
                    src={src}
                    alt={`galería ${idx + 1}`}
                    className="lightbox-gallery-image"
                    onClick={() => this.openImage(idx)}
                  />
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Lightbox overlay */}
        {currentIndex !== null && sources[currentIndex] && (
          <div
            className="lightbox-gallery-overlay"
            onClick={this.closeLightbox}
          >
            {/* Botones de navegación */}
            {sources.length > 1 && (
              <>
                <button
                  className="gallery-lightbox-nav left"
                  onClick={this.prevImage}
                >
                  ‹
                </button>
                <button
                  className="gallery-lightbox-nav right"
                  onClick={this.nextImage}
                >
                  ›
                </button>
              </>
            )}

            {/* Imagen en grande */}
            <img
              src={sources[currentIndex]}
              alt={`galería ${currentIndex + 1}`}
              className="gallery-lightbox-image"
            />

            {/* Botón de descarga si está habilitado */}
            {downloadable && (
              <a
                href={sources[currentIndex]}
                download
                className="gallery-download-button"
                onClick={(e) => e.stopPropagation()}
              >
                Descargar
              </a>
            )}

            {/* Miniaturas en el lightbox */}
            {showThumbnails && sources.length > 1 && (
              <div
                className="gallery-lightbox-thumbs"
                onClick={(e) => e.stopPropagation()}
              >
                {sources.map((thumb, idx) => (
                  <img
                    key={idx}
                    src={thumb}
                    alt={`thumb ${idx + 1}`}
                    className={`gallery-lightbox-thumb${
                      currentIndex === idx ? " active" : ""
                    }`}
                    onClick={() => this.setState({ currentIndex: idx })}
                  />
                ))}
              </div>
            )}

            {/* Indicadores en lugar de miniaturas */}
            {!showThumbnails && showIndicators && sources.length > 1 && (
              <div
                className="gallery-lightbox-dots"
                onClick={(e) => e.stopPropagation()}
              >
                {sources.map((_, idx) => (
                  <span
                    key={idx}
                    className={`gallery-lightbox-dot${
                      currentIndex === idx ? " active" : ""
                    }`}
                    onClick={() => this.setState({ currentIndex: idx })}
                  ></span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default LightboxGallery;
